// Convenios: empresas empleadoras / aseguradoras (Centro Médico → Catálogos).
// Cada convenio es la extensión médica de un partner/cliente de Financiero.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { AutoTextarea, Campo, Convenio, Modal, Switch, TIPOS_CONVENIO, fieldCls, medRpc } from './shared';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };
interface PartnerOpt { id: string; razon_social: string; identificacion: string | null; tipo_partner: string[] }
const tipoLabel = (t: string) => TIPOS_CONVENIO.find((x) => x.v === t)?.label ?? t;
const vacio = { partner_id: '', tipo: 'empresa', comision_pct: 0, contacto_nombre: '', contacto_telefono: '', contacto_email: '', observaciones: '', es_activo: true };

export function Convenios() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [filtro, setFiltro] = useState('todos');
  const { data: rows = [], isFetching } = useQuery<Convenio[]>({
    queryKey: ['med-convenios', filtro], queryFn: () => medRpc<Convenio[]>('med_convenios', { _tipo: filtro === 'todos' ? null : filtro }),
  });
  const { data: partners = [] } = useQuery<PartnerOpt[]>({
    queryKey: ['fin-partners'], queryFn: async () => (await apiClient.post('/rpc/sri_partners', {}, FIN)).data || [], staleTime: 60_000,
  });
  const clientes = partners.filter((p) => (p.tipo_partner || []).includes('cliente'));

  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof vacio>(vacio);
  const [abierto, setAbierto] = useState(false);
  const set = (patch: Partial<typeof vacio>) => setForm((f) => ({ ...f, ...patch }));

  const abrir = (c?: Convenio) => {
    if (c) { setEditId(c.id); setForm({ partner_id: c.partner_id, tipo: c.tipo, comision_pct: Number(c.comision_pct), contacto_nombre: c.contacto_nombre ?? '', contacto_telefono: c.contacto_telefono ?? '', contacto_email: c.contacto_email ?? '', observaciones: c.observaciones ?? '', es_activo: c.es_activo }); }
    else { setEditId(null); setForm(vacio); }
    setAbierto(true);
  };
  const guardar = useMutation({
    mutationFn: () => medRpc('med_convenio_guardar', { _data: { id: editId, ...form } }),
    onSuccess: () => { toast.success('Convenio guardado'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['med-convenios'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => medRpc('med_convenio_eliminar', { _id: id }),
    onSuccess: () => { toast.success('Convenio eliminado'); void qc.invalidateQueries({ queryKey: ['med-convenios'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><Building2 className="w-5 h-5" /> Convenios</h1>
        <div className="flex items-center gap-2">
          <select className={`${fieldCls} w-44`} value={filtro} onChange={(e) => setFiltro(e.target.value)}>
            <option value="todos">Todos</option>{TIPOS_CONVENIO.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
          </select>
          <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nuevo convenio</Button>
        </div>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Convenios ({rows.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Razón social</th><th className="py-2 pr-3">Identificación</th><th className="py-2 pr-3">Tipo</th>
              <th className="py-2 pr-3 text-right">Comisión %</th><th className="py-2 pr-3">Contacto</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Sin convenios</td></tr>}
              {rows.map((c) => (
                <tr key={c.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-medium">{c.razon_social}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{c.identificacion ?? '—'}</td>
                  <td className="py-2 pr-3"><span className="rounded-full bg-muted px-2 py-0.5 text-xs">{tipoLabel(c.tipo)}</span></td>
                  <td className="py-2 pr-3 text-right tabular-nums">{Number(c.comision_pct)}%</td>
                  <td className="py-2 pr-3 text-xs text-muted-foreground">{c.contacto_nombre ?? '—'}{c.contacto_telefono ? ` · ${c.contacto_telefono}` : ''}</td>
                  <td className="py-2 pr-3">{c.es_activo ? 'Activo' : 'Inactivo'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" aria-label="Editar" onClick={() => abrir(c)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title="Eliminar" aria-label="Eliminar" onClick={async () => { if (!(await dlg.confirmar(`¿Eliminar el convenio de "${c.razon_social}"?`, { peligro: true }))) return; eliminar.mutate(c.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? 'Editar convenio' : 'Nuevo convenio'} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <Campo label="Empresa / Aseguradora (cliente) *">
              <select className={fieldCls} value={form.partner_id} onChange={(e) => set({ partner_id: e.target.value })} disabled={!!editId}>
                <option value="">— Selecciona —</option>
                {clientes.map((p) => <option key={p.id} value={p.id}>{p.razon_social} ({p.identificacion ?? 's/i'})</option>)}
              </select>
            </Campo>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Tipo">
                <select className={fieldCls} value={form.tipo} onChange={(e) => set({ tipo: e.target.value })}>
                  {TIPOS_CONVENIO.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
                </select>
              </Campo>
              <Campo label="Comisión %"><input type="number" step="0.001" min="0" max="100" className={fieldCls} value={form.comision_pct} onChange={(e) => set({ comision_pct: Math.min(100, Math.max(0, Number(e.target.value))) })} /></Campo>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Campo label="Contacto"><input className={fieldCls} value={form.contacto_nombre} onChange={(e) => set({ contacto_nombre: e.target.value })} /></Campo>
              <Campo label="Teléfono"><input className={fieldCls} value={form.contacto_telefono} onChange={(e) => set({ contacto_telefono: e.target.value })} /></Campo>
              <Campo label="Email"><input className={fieldCls} value={form.contacto_email} onChange={(e) => set({ contacto_email: e.target.value })} /></Campo>
            </div>
            <Campo label="Observaciones"><AutoTextarea value={form.observaciones} onChange={(e) => set({ observaciones: e.target.value })} /></Campo>
            <div className="flex items-center justify-between border-t pt-3">
              <Switch checked={form.es_activo} onChange={(v) => set({ es_activo: v })} label="Activo" />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
                <Button isLoading={guardar.isPending} disabled={!form.partner_id} onClick={() => guardar.mutate()}>Guardar</Button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
